/**
 * Hesaplar ve Hareketler ekranlarının ortak sorusu: yeni hesap eklenebilir
 * mi, hangi hesaplar düzenlenebilir (süre bitince sınırı aşan kısım salt
 * okunur), varlık sınırı ne.
 */

import { useCallback, useEffect, useState } from "react";
import type { Account } from "@parafon/contracts/local-api";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useSinirlar } from "./KabiliyetSaglayici";
import { DUZENLENEBILIR_HESAPLAR_AYARI, duzenlenebilirHesaplar, hesapEklenebilir, secimOku } from "./hesapSinirlari";

export interface HesapErisimi {
  /** Hesap sınırı (`null` = sınırsız). */
  readonly hesapSiniri: number | null;
  /** Hesap başına varlık sınırı (`null` = sınırsız). */
  readonly varlikSiniri: number | null;
  readonly etkinHesaplar: readonly Account[];
  readonly yeniHesap: boolean;
  /** `null` = hepsi düzenlenebilir; değilse yalnızca bu kimlikler. */
  readonly duzenlenebilir: ReadonlySet<string> | null;
  duzenlenebilirMi(accountId: string): boolean;
  secimKaydet(ids: readonly string[]): Promise<void>;
}

export function useHesapErisimi(): HesapErisimi {
  const { repo, revision, invalidate } = useRepositoryContext();
  const s = useSinirlar();
  const [etkin, setEtkin] = useState<readonly Account[]>([]);
  const [secim, setSecim] = useState<string[] | null>(null);

  useEffect(() => {
    let iptal = false;
    // Okuma ya da sözleşmeye aykırı veri hatası burada yutulur: sınır
    // varsayılanlarda kalır, hatayı ekranın kendisi gösterir (hata sınırı).
    void Promise.all([repo.accounts.list(false), repo.settings.get(DUZENLENEBILIR_HESAPLAR_AYARI)])
      .then(([hesaplar, deger]) => {
        if (iptal) return;
        setEtkin(hesaplar.filter((a) => a.devreDisi !== true));
        setSecim(secimOku(deger));
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [repo, revision]);

  const duzenlenebilir = duzenlenebilirHesaplar(etkin, secim, s.hesap);
  const secimKaydet = useCallback(
    async (ids: readonly string[]) => {
      await repo.settings.set(DUZENLENEBILIR_HESAPLAR_AYARI, JSON.stringify(ids));
      setSecim([...ids]);
      invalidate();
    },
    [repo, invalidate],
  );
  return {
    hesapSiniri: s.hesap,
    varlikSiniri: s.hesapBasinaVarlik,
    etkinHesaplar: etkin,
    yeniHesap: hesapEklenebilir(etkin.length, s.hesap),
    duzenlenebilir,
    duzenlenebilirMi: (id) => duzenlenebilir === null || duzenlenebilir.has(id),
    secimKaydet,
  };
}
